import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { submitLead } from "../lib/lead";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { PRICING } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import { MessageCircle } from "lucide-react";
import { WHATSAPP_LINK } from "../config/contact";
import { useTranslation } from "react-i18next";

const popupSchema = z.object({
  nombre: z.string().min(2, "Nombre requerido"),
  email: z.string().email("Email inválido"),
  url: z.string().url("URL inválida"),
  plan: z.string().min(1, "Selecciona un plan"),
  urgencia: z.string().min(1, "Selecciona urgencia"),
  canal: z.string().min(1, "Selecciona canal"),
  mensaje: z.string().min(5, "Mensaje muy corto"),
  consentimiento: z.boolean().refine(v => v, { message: "Consentimiento requerido" })
});

export default function QuickContactPopup({ open, onOpenChange, prefillPlan = "", onSubmitted }) {
  const { t } = useTranslation(['common', 'landing']);
  const { toast } = useToast();
  
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    resolver: zodResolver(popupSchema),
    defaultValues: {
      nombre: "",
      email: "",
      url: "",
      plan: prefillPlan || "plata",
      urgencia: "24h",
      canal: "Email",
      mensaje: "",
      consentimiento: false,
    },
  });
  
  const consentimiento = watch("consentimiento");

  React.useEffect(() => {
    if (prefillPlan) setValue("plan", prefillPlan);
  }, [prefillPlan, setValue]);

  const onSubmit = async (data) => {
    const result = await submitLead({ telefono: "", ...data }, "quick-popup");
    if (result.delivered) {
      toast({ 
        title: t('common:form.success'), 
        description: t('common:form.successDesc')
      });
    } else {
      toast({ 
        title: t('common:form.error', 'No pudimos enviar tu solicitud'), 
        description: "Por favor contáctanos por WhatsApp para atención inmediata."
      });
    }
    onOpenChange(false);
    onSubmitted?.(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg bg-background border-2 border-border shadow-2xl">
        <DialogHeader className="pr-8">
          <DialogTitle className="text-foreground text-xl sm:text-2xl font-bold">{t('common:popup.title')}</DialogTitle>
          <DialogDescription className="text-foreground/70 text-sm sm:text-base">{t('common:popup.description')}</DialogDescription>
        </DialogHeader>

        {/* Badge de conversión con WhatsApp */}
        <div className="mb-3 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 text-xs sm:text-sm">
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary/10 border border-secondary/30 px-3 sm:px-4 py-1.5 sm:py-2 text-secondary font-medium">
            <MessageCircle className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="text-xs sm:text-sm">{t('common:popup.whatsappBadge')}</span>
          </div>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener" className="text-secondary hover:text-secondary/80 underline-offset-2 hover:underline font-medium text-xs sm:text-sm">{t('common:popup.openWhatsApp')}</a>
        </div>

        <form className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <Label htmlFor="q-nombre" className="text-foreground font-medium text-sm">{t('common:form.name')}</Label>
            <Input id="q-nombre" placeholder={t('common:form.namePlaceholder')} {...register("nombre")} className="bg-background border-border text-foreground placeholder:text-foreground/40 h-9 sm:h-10" />
            {errors.nombre && <p className="text-destructive text-xs mt-1">{errors.nombre.message}</p>}
          </div>
          <div>
            <Label htmlFor="q-email" className="text-foreground font-medium text-sm">{t('common:form.email')}</Label>
            <Input id="q-email" placeholder={t('common:form.emailPlaceholder')} {...register("email")} className="bg-background border-border text-foreground placeholder:text-foreground/40 h-9 sm:h-10" />
            {errors.email && <p className="text-destructive text-xs mt-1">{errors.email.message}</p>}
          </div>
          <div>
            <Label htmlFor="q-url" className="text-foreground font-medium text-sm">{t('common:form.siteUrl')}</Label>
            <Input id="q-url" placeholder={t('common:form.urlPlaceholder')} {...register("url")} className="bg-background border-border text-foreground placeholder:text-foreground/40 h-9 sm:h-10" />
            {errors.url && <p className="text-destructive text-xs mt-1">{errors.url.message}</p>}
          </div>
          <div>
            <Label className="text-foreground font-medium text-sm">{t('common:popup.plan')}</Label>
            <Select onValueChange={(v) => setValue("plan", v)} defaultValue={prefillPlan || "plata"}>
              <SelectTrigger className="bg-background border-border text-foreground h-9 sm:h-10">
                <SelectValue placeholder={t('common:popup.choosePlan')} />
              </SelectTrigger>
              <SelectContent className="bg-background border-border text-foreground">
                {PRICING.map(p => <SelectItem key={p.id} value={p.id}>{t(`landing:pricing.${p.id}.name`)}</SelectItem>)}
              </SelectContent>
            </Select>
            {errors.plan && <p className="text-destructive text-xs mt-1">{errors.plan.message}</p>}
          </div>
          <div>
            <Label className="text-foreground font-medium text-sm">{t('common:form.urgency')}</Label>
            <Select onValueChange={(v) => setValue("urgencia", v)} defaultValue="24h">
              <SelectTrigger className="bg-background border-border text-foreground h-9 sm:h-10">
                <SelectValue placeholder={t('common:form.urgencyPlaceholder')} />
              </SelectTrigger>
              <SelectContent className="bg-background border-border text-foreground">
                <SelectItem value="4h">{t('common:form.urgencyImmediate')}</SelectItem>
                <SelectItem value="24h">{t('common:form.urgencyHigh')}</SelectItem>
                <SelectItem value="48-72h">{t('common:form.urgencyMedium')}</SelectItem>
              </SelectContent>
            </Select>
            {errors.urgencia && <p className="text-destructive text-xs mt-1">{errors.urgencia.message}</p>}
          </div>
          <div>
            <Label className="text-foreground font-medium text-sm">{t('common:form.preferredChannel')}</Label>
            <Select onValueChange={(v) => setValue("canal", v)} defaultValue="Email">
              <SelectTrigger className="bg-background border-border text-foreground h-9 sm:h-10">
                <SelectValue placeholder={t('common:form.channelPlaceholder')} />
              </SelectTrigger>
              <SelectContent className="bg-background border-border text-foreground">
                <SelectItem value="Email">{t('common:form.channelEmail')}</SelectItem>
                <SelectItem value="WhatsApp">{t('common:form.channelWhatsApp')}</SelectItem>
                <SelectItem value="Llamada">{t('common:form.channelCall')}</SelectItem>
              </SelectContent>
            </Select>
            {errors.canal && <p className="text-destructive text-xs mt-1">{errors.canal.message}</p>}
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="q-mensaje" className="text-foreground font-medium text-sm">{t('common:form.message')}</Label>
            <Textarea id="q-mensaje" rows={2} placeholder={t('common:popup.messagePlaceholder')} {...register("mensaje")} className="bg-background border-border text-foreground placeholder:text-foreground/40 text-sm" />
            {errors.mensaje && <p className="text-destructive text-xs mt-1">{errors.mensaje.message}</p>}
          </div>
          <div className="md:col-span-2 flex items-start gap-2">
            <Checkbox 
              id="q-consent" 
              checked={consentimiento}
              onCheckedChange={(checked) => setValue("consentimiento", checked)}
              className="mt-1"
            />
            <Label htmlFor="q-consent" className="text-xs sm:text-sm text-foreground/70 cursor-pointer leading-tight">{t('common:form.consent')}</Label>
          </div>
          {errors.consentimiento && <p className="md:col-span-2 text-destructive text-xs -mt-2">{errors.consentimiento.message}</p>}
          <div className="md:col-span-2">
            <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-5 sm:py-6 text-sm sm:text-base">{t('common:form.submitButton')}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}